import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.string().uuid();

/**
 * Discount status enum matching backend model
 */
export const DiscountStatusSchema = z.enum([
  "standard_price",
  "approved_discount",
  "unapproved_discount",
  "no_list_price",
  "customer_specific_discount_pending",
]);

/**
 * Discount info schema (for approved discounts)
 */
export const DiscountInfoSchema = z.object({
  id: UUIDSchema,
  discountPrice: z.number(),
  validUntil: z.coerce.date().nullable().optional(),
  sku: z.string(),
  attachment: z.string().nullable().optional(),
});

/**
 * Price discrepancy schema
 */
export const PriceDiscrepancySchema = z.object({
  expectedPrice: z.number(),
  actualPrice: z.number(),
  difference: z.number(),
});

/**
 * List price info schema (from ERP)
 */
export const ListPriceInfoSchema = z.object({
  price: z.number(),
  currencyCode: z.string(),
  priceListName: z.string(),
});

/**
 * End customer info schema (for customer-specific discounts)
 */
export const EndCustomerInfoSchema = z.object({
  name: z.string(),
  discountScopeId: UUIDSchema,
});

/**
 * Discount validation result schema
 */
export const DiscountValidationResultSchema = z.object({
  status: DiscountStatusSchema,
  poUnitPrice: z.number(),
  discount: DiscountInfoSchema.nullable().optional(),
  discrepancy: PriceDiscrepancySchema.nullable().optional(),
  listPrice: ListPriceInfoSchema.nullable().optional(),
  endCustomer: EndCustomerInfoSchema.nullable().optional(),
});

/**
 * Validate discounts input schema
 */
export const ValidateDiscountsInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
});

/**
 * Validate discounts response schema
 */
export const ValidateDiscountsResponseSchema = z.object({
  results: z.record(z.string(), DiscountValidationResultSchema),
});

/**
 * Customer-specific discount verification status
 */
export const CustomerSpecificDiscountVerificationStatusSchema = z.enum([
  "PENDING_VERIFICATION",
  "VERIFIED",
  "VERIFICATION_REQUESTED",
  "WAIVED",
]);

/**
 * Update discount verification input schema
 */
export const UpdateDiscountVerificationInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
  lineItemIndex: z.number().int().min(0),
  status: z.enum(["VERIFIED", "VERIFICATION_REQUESTED", "WAIVED"]),
  notes: z.string().optional(),
});

/**
 * Update discount verification response schema
 */
export const UpdateDiscountVerificationResponseSchema = z.object({
  success: z.boolean(),
});

// ============================================================================
// Extraction Metadata (for document comparison viewer)
// ============================================================================

/**
 * Polygon point for bounding box citations
 */
export const PolygonPointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

/**
 * Citation from Extend AI extraction
 */
export const CitationSchema = z.object({
  page: z.number().optional(),
  referenceText: z.string().optional(),
  polygon: z.array(PolygonPointSchema).optional(),
  /** Page width in pixels (for coordinate conversion) */
  pageWidth: z.number().optional(),
  /** Page height in pixels (for coordinate conversion) */
  pageHeight: z.number().optional(),
});

/**
 * Field metadata from Extend AI extraction
 */
export const FieldMetadataSchema = z.object({
  ocrConfidence: z.number().nullable().optional(),
  logprobsConfidence: z.number().nullable().optional(),
  citations: z.array(CitationSchema).optional(),
});

/**
 * Document info for extraction viewer
 */
export const ExtractionDocumentSchema = z.object({
  id: UUIDSchema,
  url: z.string(),
  originalFileName: z.string(),
  mimeType: z.string(),
});

/**
 * Extraction output from Extend AI
 */
export const ExtractionOutputSchema = z.object({
  value: z.record(z.string(), z.unknown()),
  metadata: z.record(z.string(), FieldMetadataSchema),
});

/**
 * Get extraction metadata input schema
 */
export const GetExtractionMetadataInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
});

/**
 * Get extraction metadata response schema
 */
export const GetExtractionMetadataResponseSchema = z.object({
  document: ExtractionDocumentSchema.nullable(),
  extraction: ExtractionOutputSchema.nullable(),
  purchaseOrderIndex: z.number(),
});

/**
 * Sales Order Drafts contracts
 */
export const salesOrderDraftsContracts = {
  validateDiscounts: oc
    .input(ValidateDiscountsInputSchema)
    .output(ValidateDiscountsResponseSchema)
    .route({
      method: "POST",
    }),
  updateDiscountVerification: oc
    .input(UpdateDiscountVerificationInputSchema)
    .output(UpdateDiscountVerificationResponseSchema)
    .route({
      method: "PATCH",
    }),
  getExtractionMetadata: oc
    .input(GetExtractionMetadataInputSchema)
    .output(GetExtractionMetadataResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

// Type exports
export type DiscountStatus = z.infer<typeof DiscountStatusSchema>;
export type DiscountInfo = z.infer<typeof DiscountInfoSchema>;
export type PriceDiscrepancy = z.infer<typeof PriceDiscrepancySchema>;
export type ListPriceInfo = z.infer<typeof ListPriceInfoSchema>;
export type EndCustomerInfo = z.infer<typeof EndCustomerInfoSchema>;
export type DiscountValidationResult = z.infer<
  typeof DiscountValidationResultSchema
>;
export type ValidateDiscountsInput = z.infer<
  typeof ValidateDiscountsInputSchema
>;
export type ValidateDiscountsResponse = z.infer<
  typeof ValidateDiscountsResponseSchema
>;
export type CustomerSpecificDiscountVerificationStatus = z.infer<
  typeof CustomerSpecificDiscountVerificationStatusSchema
>;
export type UpdateDiscountVerificationInput = z.infer<
  typeof UpdateDiscountVerificationInputSchema
>;
export type UpdateDiscountVerificationResponse = z.infer<
  typeof UpdateDiscountVerificationResponseSchema
>;
export type PolygonPoint = z.infer<typeof PolygonPointSchema>;
export type Citation = z.infer<typeof CitationSchema>;
export type FieldMetadata = z.infer<typeof FieldMetadataSchema>;
export type ExtractionDocument = z.infer<typeof ExtractionDocumentSchema>;
export type ExtractionOutput = z.infer<typeof ExtractionOutputSchema>;
export type GetExtractionMetadataInput = z.infer<
  typeof GetExtractionMetadataInputSchema
>;
export type GetExtractionMetadataResponse = z.infer<
  typeof GetExtractionMetadataResponseSchema
>;

/**
 * DiscountStatus constants for runtime comparison.
 * Use these instead of string literals for type safety.
 */
export const DiscountStatus = {
  STANDARD_PRICE: "standard_price",
  APPROVED_DISCOUNT: "approved_discount",
  UNAPPROVED_DISCOUNT: "unapproved_discount",
  NO_LIST_PRICE: "no_list_price",
  CUSTOMER_SPECIFIC_DISCOUNT_PENDING: "customer_specific_discount_pending",
} as const;
